/**
 * The decision calendar (Design Review V-17; PRD 10.9; TRD 8.6; Phase 49): by when must we act to
 * prevent each forecast overload.
 *
 * The question
 * ------------
 * The overload list says *November is 118 per cent*. That is information. A planner cannot act on
 * November; they can only act **today**, and what today asks of them depends on how long each
 * remedy takes to arrive. Booking a freelancer takes two weeks, a contractor four, an open
 * requisition ten — so a November overload is a freelancer conversation in October and a hiring
 * conversation that should have started in August. The overload is the same; the calendar is not.
 *
 * The rule
 * --------
 * `decide_by = the overload window's first week − the remedy's lead time`, in whole weeks, with
 * the role's own lead time (`remedies.by_discipline[service]`) overriding the default. That is
 * TRD 8.6, and it is the one place the rule lives: the run writes each signal's deadlines with
 * this function, and `GET /forecast/decisions` recomputes them with the same one whenever the
 * settings have moved since.
 *
 * Why it is recomputed rather than read
 * -------------------------------------
 * A run freezes its deadlines, and it should: what the forecast said on Monday is a record. But a
 * lead time is not a forecast, it is a *policy* — how long our hiring actually takes — and when an
 * admin corrects it the calendar has to move at once rather than at the next run. So the endpoint
 * recomputes from current settings and carries the run's own figure beside it, and the card says
 * when the two disagree. The same argument decides `past`: a deadline that passed yesterday is
 * red today, whatever day the run was taken.
 *
 * Pure: no DOM, no I/O.
 */
import { col } from './table.js';

/**
 * The remedies, in the order a planner reaches for them: the cheapest and fastest first, the one
 * that takes a quarter last. Matches `settings.remedies.lead_time_weeks` exactly.
 */
export const REMEDIES = Object.freeze(['shift_project', 'freelancer', 'contractor', 'open_req']);

/** What each remedy is called, and what doing it actually means. */
export const REMEDY_LABELS = Object.freeze({
  shift_project: 'Move a project',
  freelancer: 'Book a freelancer',
  contractor: 'Sign a contractor',
  open_req: 'Open a requisition',
});

export const REMEDY_NOTES = Object.freeze({
  shift_project: 'move the delivery date, and the demand with it',
  freelancer: 'a known freelancer, briefed and started',
  contractor: 'a contract signed and a person onboarded',
  open_req: 'a requisition opened, filled and the hire productive',
});

/**
 * The remedies that bring **more people**. `shift_project` is not one of them: it moves the work
 * rather than adding capacity, and it has no lead time at all, so it is never too late. That is
 * why "unpreventable" is not a useful idea here and `staffing_too_late` is: past a certain date
 * no amount of hiring arrives in time, and the only moves left are to move or rescope the work.
 */
export const STAFFING_REMEDIES = Object.freeze(['freelancer', 'contractor', 'open_req']);

/** How the calendar may be grouped. */
export const DECISION_GROUPS = Object.freeze(['date', 'remedy', 'signal']);

/** A deadline's state, which is what the reader is actually scanning for. */
export const DECISION_STATES = Object.freeze(['past', 'due', 'ahead']);

/**
 * How near a deadline has to be before it is *due* rather than merely ahead. Two weeks is a
 * planning cycle: near enough that the next meeting is the last one that can act on it.
 */
export const DUE_SOON_DAYS = 14;

const DAY = 86400000;

/** `2026-09-14` plus n days, as a plain date. No timezone: these are calendar dates. */
export function addDays(iso, days) {
  const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number);
  const at = new Date(Date.UTC(y, m - 1, d) + days * DAY);
  return at.toISOString().slice(0, 10);
}

/** Whole days between two plain dates, `to` minus `from`. */
export function daysBetween(from, to) {
  const at = (iso) => {
    const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number);
    return Date.UTC(y, m - 1, d);
  };
  return Math.round((at(to) - at(from)) / DAY);
}

/**
 * When each remedy must be decided for its lead time to land before the overload starts
 * (TRD 8.6): `decide_by = windowStart − lead_time_weeks`, the role's own lead time over the
 * default, and `past` when that date lies before `today`.
 *
 * The one definition. The forecast run writes `overload_signals.decision_deadlines_json` with it
 * (Phase 14) and the decision calendar recomputes with it (Phase 49); if they ever disagreed, one
 * of the two screens would be lying about a date somebody is going to act on.
 *
 * @param {{ windowStart: string, service?: string | null,
 *           remedies: { lead_time_weeks: Record<string, number>, by_discipline?: Record<string, Record<string, number>> },
 *           today: string }} input
 */
export function decisionDeadlines({ windowStart, service = null, remedies, today }) {
  const overrides = (service && remedies?.by_discipline?.[service]) || {};
  return REMEDIES.map((remedy) => {
    const lead = Number(overrides[remedy] ?? remedies?.lead_time_weeks?.[remedy] ?? 0);
    const decideBy = addDays(windowStart, -7 * Math.max(0, Math.round(lead)));
    return {
      remedy,
      lead_time_weeks: lead,
      decide_by: decideBy,
      past: decideBy < today,
      source: overrides[remedy] !== undefined ? 'by_discipline' : 'default',
    };
  });
}

/** Past, due soon, or still ahead — the three states the calendar colours by. */
export function decisionState(decideBy, today) {
  if (decideBy < today) return 'past';
  return daysBetween(today, decideBy) <= DUE_SOON_DAYS ? 'due' : 'ahead';
}

// ---------------------------------------------------------------------------------------------
// Model

const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;

/**
 * Everything the calendar draws, from a `GET /forecast/decisions` response.
 *
 * @param {object} data
 * @param {{ group?: string, remedies?: string[], states?: string[], q?: string }} [opts]
 */
export function decisionModel(
  data,
  { group = 'date', remedies = null, states = null, q = '' } = {},
) {
  if (!data?.today) return null;
  const wantedRemedies = new Set(remedies?.length ? remedies : REMEDIES);
  const wantedStates = new Set(states?.length ? states : DECISION_STATES);
  const needle = String(q ?? '')
    .trim()
    .toLowerCase();

  const all = (data.decisions ?? []).map((d) => ({
    ...d,
    label: REMEDY_LABELS[d.remedy] ?? d.remedy,
    note: REMEDY_NOTES[d.remedy] ?? '',
    days_from_today: daysBetween(data.today, d.decide_by),
    // A lead time an admin has changed since the run moves the date; the card says which is which.
    moved: d.stored_decide_by !== null && d.stored_decide_by !== d.decide_by,
  }));

  const shown = all
    .filter((d) => wantedRemedies.has(d.remedy))
    .filter((d) => wantedStates.has(d.state))
    .filter((d) => !needle || String(d.entity_label).toLowerCase().includes(needle))
    .sort(
      (a, b) =>
        String(a.decide_by).localeCompare(String(b.decide_by)) ||
        b.hours_over - a.hours_over ||
        REMEDIES.indexOf(a.remedy) - REMEDIES.indexOf(b.remedy) ||
        String(a.entity_label).localeCompare(String(b.entity_label)),
    );

  // The groups the calendar lays out in. Every grouping keeps the date order inside it, because
  // the date is what the reader came for whichever way the rows are stacked.
  const keyOf = (d) =>
    group === 'remedy' ? d.remedy : group === 'signal' ? d.signal_key : d.decide_by;
  const labelOf = (d) =>
    group === 'remedy' ? d.label : group === 'signal' ? d.entity_label : d.decide_by;
  const groups = [];
  const index = new Map();
  for (const d of shown) {
    const key = keyOf(d);
    if (!index.has(key)) {
      const entry = { key, label: labelOf(d), rows: [], past: 0, due: 0, ahead: 0 };
      index.set(key, entry);
      groups.push(entry);
    }
    const entry = index.get(key);
    entry.rows.push(d);
    entry[d.state] += 1;
  }

  return {
    run_id: data.run_id ?? null,
    today: data.today,
    from: data.from,
    to: data.to,
    weeks_count: data.weeks_count ?? 0,
    group,
    remedies: [...wantedRemedies],
    states: [...wantedStates],
    lead_times: data.lead_times ?? null,
    settings_moved: Boolean(data.settings_moved),
    decisions: shown,
    all,
    groups,
    signals: data.signals ?? [],
    hidden: all.length - shown.length,
    totals: data.totals ?? null,
  };
}

/** The figures the card's header and notes state, counted over what is drawn. */
export function decisionSummary(model) {
  const rows = model?.decisions ?? [];
  const past = rows.filter((d) => d.state === 'past');
  const due = rows.filter((d) => d.state === 'due');
  // The next thing anybody has to decide, which is the one line a planner reads if they read one.
  const next = rows.find((d) => d.state !== 'past') ?? null;
  const signals = new Set(rows.map((d) => d.signal_key));
  const missed = new Set(past.map((d) => d.signal_key));
  return {
    decisions: rows.length,
    signals: signals.size,
    past: past.length,
    due: due.length,
    ahead: rows.length - past.length - due.length,
    next,
    // The overloads no hire can reach in time: every remedy that brings people has passed, and
    // only moving or rescoping the work is left. Not "unpreventable" — moving a project has no
    // lead time, so something can always be done; it is just not a staffing something.
    staffing_too_late: [...signals].filter((key) =>
      rows
        .filter((d) => d.signal_key === key && STAFFING_REMEDIES.includes(d.remedy))
        .every((d, _i, own) => own.length > 0 && d.state === 'past'),
    ),
    signals_with_a_missed_deadline: missed.size,
    hours_over: round2(
      [...signals].reduce(
        (s, key) => s + (rows.find((d) => d.signal_key === key)?.hours_over ?? 0),
        0,
      ),
    ),
    moved: rows.filter((d) => d.moved).length,
    hidden: model?.hidden ?? 0,
  };
}

// ---------------------------------------------------------------------------------------------
// Geometry

const r = (n) => Math.round((n + Number.EPSILON) * 100) / 100;

/**
 * The calendar as a timeline: today at the left edge or inside it, one lane per group, one
 * marker per decision at its own date.
 *
 * The axis always includes **today**, whatever the deadlines do. A calendar of decisions that
 * were all due last month would otherwise draw itself off to the right of a reader who is
 * standing in the present and needs to see how far past it they are.
 *
 * @param {ReturnType<typeof decisionModel>} model
 * @param {{ width?: number, laneHeight?: number, top?: number }} [opts]
 */
export function calendarLayout(model, { width = 640, laneHeight = 34, top = 0 } = {}) {
  const groups = model?.groups ?? [];
  const rows = model?.decisions ?? [];
  if (!rows.length) return { lanes: [], markers: [], width, height: top, domain: null };

  const dates = rows.map((d) => d.decide_by);
  const min = dates.reduce((m, d) => (d < m ? d : m), model.today);
  const max = dates.reduce((m, d) => (d > m ? d : m), model.today);
  // A calendar of one day is still a calendar: pad it so the marker is not on the frame.
  const span = Math.max(daysBetween(min, max), 1);
  const pad = Math.max(2, Math.round(span * 0.04));
  const from = addDays(min, -pad);
  const to = addDays(max, pad);
  const total = daysBetween(from, to);
  const x = (iso) => r((daysBetween(from, iso) / total) * width);

  const lanes = groups.map((g, i) => ({
    key: g.key,
    label: g.label,
    group: g,
    index: i,
    top: top + i * laneHeight,
    centre: top + i * laneHeight + laneHeight / 2,
    height: laneHeight,
  }));
  const laneBy = new Map(lanes.map((l) => [l.key, l]));

  const markers = rows.map((d) => {
    const key =
      model.group === 'remedy' ? d.remedy : model.group === 'signal' ? d.signal_key : d.decide_by;
    const lane = laneBy.get(key);
    return {
      key: `${d.signal_key}|${d.remedy}`,
      decision: d,
      lane,
      x: x(d.decide_by),
      y: lane?.centre ?? top,
      state: d.state,
    };
  });

  return {
    lanes,
    markers,
    width,
    height: top + lanes.length * laneHeight,
    today_x: x(model.today),
    domain: { from, to, days: total },
    x,
  };
}

// ---------------------------------------------------------------------------------------------
// Table columns (Phase 37)

/** One row per signal and remedy: the calendar as a list, which is what exports. */
export function decisionColumns() {
  return [
    col.date('decide_by', 'Decide by'),
    col.text('state', 'State'),
    col.count('days_from_today', 'Days from today'),
    col.text('remedy', 'Remedy'),
    col.count('lead_time_weeks', 'Lead time (weeks)'),
    col.text('lead_time_source', 'Lead time from'),
    col.text('entity_label', 'Where'),
    col.text('dimension', 'Dimension'),
    col.week('window_start', 'Overload starts'),
    col.count('duration_weeks', 'Weeks over'),
    col.hours('hours_over', 'Hours over'),
    col.percent('utilization', 'Peak allocation'),
    col.text('severity', 'Severity'),
    col.date('stored_decide_by', 'The run said'),
  ];
}

/** One row per decision, in the order the calendar reads. */
export function decisionRows(model) {
  return (model?.all ?? []).map((d) => ({
    decide_by: d.decide_by,
    state: d.state,
    days_from_today: d.days_from_today,
    remedy: REMEDY_LABELS[d.remedy] ?? d.remedy,
    lead_time_weeks: d.lead_time_weeks,
    lead_time_source: d.lead_time_source,
    entity_label: d.entity_label,
    dimension: d.dimension,
    window_start: d.window_start,
    duration_weeks: d.duration_weeks,
    hours_over: d.hours_over,
    utilization: d.utilization,
    severity: d.severity,
    stored_decide_by: d.stored_decide_by,
  }));
}
